import { Bar } from 'react-chartjs-2';
import { useGbpReviewsSummaryQuery } from '../api/queries';
import type { GbpPoorPerformer } from '../types';

type Props = {
  onDrillIn: (afNumber: string) => void;
};

export default function GbpReviewsSection({ onDrillIn }: Props) {
  const { data, isLoading, error } = useGbpReviewsSummaryQuery(true);

  if (isLoading) {
    return (
      <div className="ds-section-header">
        <h2>Top Performers — Google Reviews</h2>
        <span className="meta">Loading…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-section-header">
        <h2>Top Performers — Google Reviews</h2>
        <span className="meta" style={{ color: '#dc2626' }}>
          Failed to load: {(error as Error).message}
        </span>
      </div>
    );
  }
  if (!data) return null;

  const { topPerformers, poorPerformers } = data;

  return (
    <div className="ds-reviews-row">
      <div className="ds-reviews-col">
        <div className="ds-section-header">
          <h2>Top Performers — Google Reviews</h2>
          <span className="meta" style={{ marginLeft: 0 }}>
            {topPerformers.length > 0
              ? 'Top 10 franchisees by Google review count'
              : 'No review data yet'}
          </span>
        </div>

        {topPerformers.length > 0 && (
          <div className="ds-panel ds-reviews-panel ds-reviews-panel--chart">
            <div className="ds-reviews-chart-wrap">
              <Bar
                data={{
                  labels: topPerformers.map((d) => d.name),
                  datasets: [
                    {
                      label: 'Google reviews',
                      data: topPerformers.map((d) => d.count),
                      backgroundColor: '#2a5a8c',
                      borderRadius: 6,
                    },
                  ],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { beginAtZero: true, ticks: { font: { size: 11 } } },
                    y: { ticks: { font: { size: 11 } } },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>

      {poorPerformers.length > 0 && (
        <div className="ds-reviews-col">
          <div className="ds-section-header">
            <h2 style={{ color: '#dc2626' }}>Poor Google Reviews — Needs Attention</h2>
            <span className="meta">
              {poorPerformers.length} franchisee{poorPerformers.length === 1 ? '' : 's'} with
              fewer than 10 reviews · open 4+ months
            </span>
          </div>
          <div className="ds-panel ds-reviews-panel ds-reviews-panel--table">
            <table className="ds-table ds-table-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Franchisee</th>
                  <th className="num">Reviews</th>
                  <th className="num">Days Open</th>
                  <th>Open Date</th>
                </tr>
              </thead>
              <tbody>
                {poorPerformers.map((p) => (
                  <PoorRow key={p.afNumber} row={p} onDrillIn={onDrillIn} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PoorRow({
  row,
  onDrillIn,
}: {
  row: GbpPoorPerformer;
  onDrillIn: (af: string) => void;
}) {
  return (
    <tr onClick={() => onDrillIn(row.afNumber)}>
      <td>
        <span className="ds-marker red" />
      </td>
      <td>
        <div className="ds-name">{row.name}</div>
        <div className="ds-sub">{row.afNumber}</div>
      </td>
      <td className="num">
        <span className="num-val" style={{ color: '#dc2626', fontWeight: 700 }}>
          {row.count}
        </span>
      </td>
      <td className="num">
        <span className="num-val">{row.daysOpen}</span>
      </td>
      <td style={{ fontSize: 12, color: '#5a6a7a' }}>{row.openDate}</td>
    </tr>
  );
}
